import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Select,
  Upload,
  Button,
  Space,
  List,
  Popconfirm,
  Typography,
  Alert,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import {
  DOCUMENT_AND_IMAGE_ACCEPT,
  beforeUploadDocumentOrImage,
  filterDocumentOrImageFileList,
} from "../../../utils/fileUpload";

const { Text } = Typography;

const HolidayPackageFormModal = ({
  visible,
  onClose,
  onSubmit,
  onDeleteFile,
  saving,
  deletingFileId,
  pkg, // existing record when editing
  academicYears,
  terms,
  classes,
  subjects,
}) => {
  const [form] = Form.useForm();
  const [isPreschool, setIsPreschool] = useState(false);
  const [fileList, setFileList] = useState([]);

  const isEditing = !!pkg;

  useEffect(() => {
    if (visible) {
      if (isEditing) {
        form.setFieldsValue({
          classID: pkg.classID,
          subjectID: pkg.subjectID,
          acadyrID: pkg.acadyrID,
          termID: pkg.termID,
        });
        // detect preschool from the loaded class list
        const cls = classes.find((c) => c.id === pkg.classID);
        setIsPreschool(cls?.classGroup === "PreSchool");
      } else {
        form.resetFields();
        setIsPreschool(false);
      }
      setFileList([]);
    }
  }, [visible, pkg, isEditing, form, classes]);

  const handleClassChange = (classId) => {
    const cls = classes.find((c) => c.id === classId);
    const preschool = cls?.classGroup === "PreSchool";
    setIsPreschool(preschool);
    if (preschool) {
      form.setFieldValue("subjectID", undefined);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();
      formData.append("classID", values.classID);
      formData.append("acadyrID", values.acadyrID);
      formData.append("termID", values.termID);
      if (!isPreschool && values.subjectID) {
        formData.append("subjectID", values.subjectID);
      }
      fileList.forEach((f) => {
        const rawFile = f.originFileObj || f;
        formData.append("files", rawFile);
      });
      await onSubmit(formData, pkg?.id);
    } catch {
      // validation failed
    }
  };

  const uploadProps = {
    beforeUpload: beforeUploadDocumentOrImage,
    multiple: true,
    fileList,
    onChange: ({ fileList: newList }) =>
      setFileList(filterDocumentOrImageFileList(newList)),
    accept: DOCUMENT_AND_IMAGE_ACCEPT,
  };

  return (
    <Modal
      title={isEditing ? "Edit Holiday Package" : "New Holiday Package"}
      open={visible}
      onCancel={onClose}
      onOk={handleOk}
      okText={isEditing ? "Save Changes" : "Create"}
      confirmLoading={saving}
      width={700}
      destroyOnClose
    >
      <Alert
        message="Title is auto-generated from class, subject, term, and academic year."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        <Form.Item
          name="classID"
          label="Class"
          rules={[{ required: true, message: "Select a class" }]}
        >
          <Select
            placeholder="Select class"
            showSearch
            optionFilterProp="label"
            onChange={handleClassChange}
          >
            {classes.map((c) => (
              <Select.Option key={c.id} value={c.id} label={c.name}>
                {c.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {!isPreschool && (
          <Form.Item
            name="subjectID"
            label="Subject"
            rules={[{ required: true, message: "Select a subject" }]}
          >
            <Select
              placeholder="Select subject"
              showSearch
              optionFilterProp="label"
            >
              {subjects.map((s) => (
                <Select.Option key={s.id} value={s.id} label={s.name}>
                  {s.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {isPreschool && (
          <Alert
            message="No subject required for Preschool classes."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="acadyrID"
          label="Academic Year"
          rules={[{ required: true, message: "Select an academic year" }]}
        >
          <Select
            placeholder="Select academic year"
            showSearch
            optionFilterProp="label"
          >
            {academicYears.map((y) => (
              <Select.Option key={y.id} value={y.id} label={y.acadyr}>
                {y.acadyr}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="termID"
          label="Term"
          rules={[{ required: true, message: "Select a term" }]}
        >
          <Select placeholder="Select term" showSearch optionFilterProp="label">
            {terms.map((t) => (
              <Select.Option key={t.id} value={t.id} label={t.name}>
                {t.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {isEditing && pkg?.files?.length > 0 && (
          <Form.Item label="Existing Files">
            <List
              size="small"
              dataSource={pkg.files}
              renderItem={(file) => (
                <List.Item
                  actions={[
                    <Popconfirm
                      title="Remove this file?"
                      onConfirm={() => onDeleteFile(pkg.id, file.id)}
                      okText="Remove"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        type="text"
                        danger
                        size="small"
                        loading={deletingFileId === file.id}
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>,
                  ]}
                >
                  <Space>
                    <PaperClipOutlined />
                    <Text>{file.filename}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </Form.Item>
        )}

        <Form.Item label="Attach Files">
          <Upload {...uploadProps} listType="text">
            <Button icon={<UploadOutlined />}>Select Files</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default HolidayPackageFormModal;
